import { Action, ActionCreator, Dispatch } from 'redux'
import { IResponseAction } from '../interfaces'
import { IProductDimension } from 'shared/models/IProductDimension'
import { ThunkAction } from 'redux-thunk'
import { IApplicationState } from '../store'
import GlnService from 'shared/service/GlnService'

export enum Actions {
  GetProductDimensionsRequest = 'GetProductDimensionsRequest',
  GetProductDimensionsResponse = 'GetProductDimensionsResponse',
  GetProductDimensionRequest = 'GetProductDimensionRequest',
  GetProductDimensionResponse = 'GetProductDimensionResponse',
  PostProductDimensionRequest = 'PostProductDimensionRequest',
  PostProductDimensionResponse = 'PostProductDimensionResponse',
  UpdateProductDimensionRequest = 'UpdateProductDimensionRequest',
  UpdateProductDimensionResponse = 'UpdateProductDimensionResponse',
  DeleteProductDimensionRequest = 'DeleteProductDimensionRequest',
  DeleteProductDimensionResponse = 'DeleteProductDimensionResponse',
  ClearError = 'ClearProductDimensionError',
}

export interface IGetProductDimensionsRequestAction
  extends Action<Actions.GetProductDimensionsRequest> {}

export interface IGetProductDimensionsResponseAction
  extends IResponseAction<Actions.GetProductDimensionsResponse> {
  payload: IProductDimension[]
}
export interface IGetProductDimensionRequestAction
  extends Action<Actions.GetProductDimensionRequest> {
  payload: number
}
export interface IGetProductDimensionResponseAction
  extends IResponseAction<Actions.GetProductDimensionResponse> {
  payload: IProductDimension
}

export interface IPostProductDimensionRequestAction
  extends Action<Actions.PostProductDimensionRequest> {
  payload: IProductDimension
}
export interface IPostProductDimensionResponseAction
  extends IResponseAction<Actions.PostProductDimensionResponse> {
  payload: IProductDimension
}
export interface IUpdateProductDimensionRequestAction
  extends Action<Actions.UpdateProductDimensionRequest> {
  payload: IProductDimension
}
export interface IUpdateProductDimensionResponseAction
  extends IResponseAction<Actions.UpdateProductDimensionResponse> {
  payload: IProductDimension
}
export interface IDeleteProductDimensionRequestAction
  extends Action<Actions.DeleteProductDimensionRequest> {
  payload: number
}
export interface IDeleteProductDimensionResponseAction
  extends IResponseAction<Actions.DeleteProductDimensionResponse> {}

export interface IClearErrorAction extends Action<Actions.ClearError> {}

export type ProductDimensionActions =
  | IGetProductDimensionsRequestAction
  | IGetProductDimensionsResponseAction
  | IGetProductDimensionRequestAction
  | IGetProductDimensionResponseAction
  | IPostProductDimensionRequestAction
  | IPostProductDimensionResponseAction
  | IUpdateProductDimensionRequestAction
  | IUpdateProductDimensionResponseAction
  | IDeleteProductDimensionRequestAction
  | IDeleteProductDimensionResponseAction
  | IClearErrorAction

export const getProductDimensionsActionCreator: ActionCreator<
  ThunkAction<
    Promise<IGetProductDimensionsResponseAction>,
    IApplicationState,
    null,
    IGetProductDimensionsResponseAction
  >
> = () => {
  return async (dispatch: Dispatch) => {
    const requestAction: IGetProductDimensionsRequestAction = {
      type: Actions.GetProductDimensionsRequest,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.getProductDimensions()
      const response: IGetProductDimensionsResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.GetProductDimensionsResponse,
      }

      return dispatch(response)
    } catch (e) {
      const errorReponse: IGetProductDimensionsResponseAction = {
        success: false,
        errorMessage: `Error fetching product dimensions: ${e.message}`,
        payload: null,
        type: Actions.GetProductDimensionsResponse,
      }
      return dispatch(errorReponse)
    }
  }
}

export const getProductDimensionActionCreator: ActionCreator<
  ThunkAction<
    Promise<IGetProductDimensionResponseAction>,
    IApplicationState,
    null,
    IGetProductDimensionResponseAction
  >
> = (productDimensionId: number) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IGetProductDimensionRequestAction = {
      type: Actions.GetProductDimensionRequest,
      payload: productDimensionId,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.getProductDimension(productDimensionId)
      const response: IGetProductDimensionResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.GetProductDimensionResponse,
      }
      return dispatch(response)
    } catch (e) {
      const errorResponse: IGetProductDimensionResponseAction = {
        success: false,
        errorMessage: `Error getting product dimension: ${e.message}`,
        payload: null,
        type: Actions.GetProductDimensionResponse,
      }
      return dispatch(errorResponse)
    }
  }
}

export const postProductDimensionActionCreator: ActionCreator<
  ThunkAction<
    Promise<IPostProductDimensionResponseAction>,
    IApplicationState,
    null,
    IPostProductDimensionResponseAction
  >
> = (productDimension: IProductDimension) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IPostProductDimensionRequestAction = {
      type: Actions.PostProductDimensionRequest,
      payload: productDimension,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.postProductDimension(productDimension)
      const response: IPostProductDimensionResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.PostProductDimensionResponse,
      }
      return dispatch(response)
    } catch (e) {
      const errorResponse: IPostProductDimensionResponseAction = {
        success: false,
        errorMessage: `Error posting product dimension: ${e.message}`,
        payload: null,
        type: Actions.PostProductDimensionResponse,
      }
      return dispatch(errorResponse)
    }
  }
}

export const updateProductDimensionActionCreator: ActionCreator<
  ThunkAction<
    Promise<IUpdateProductDimensionResponseAction>,
    IApplicationState,
    null,
    IUpdateProductDimensionResponseAction
  >
> = (productDimension: IProductDimension) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IUpdateProductDimensionRequestAction = {
      type: Actions.UpdateProductDimensionRequest,
      payload: productDimension,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.updateProductDimension(productDimension)
      const response: IUpdateProductDimensionResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        payload: result.payLoad,
        type: Actions.UpdateProductDimensionResponse,
      }
      return dispatch(response)
    } catch (e) {
      const errorResponse: IUpdateProductDimensionResponseAction = {
        success: false,
        errorMessage: `Error updating product dimension: ${e.message}`,
        payload: null,
        type: Actions.UpdateProductDimensionResponse,
      }
      return dispatch(errorResponse)
    }
  }
}

export const deleteProductDimensionActionCreator: ActionCreator<
  ThunkAction<
    Promise<IDeleteProductDimensionResponseAction>,
    IApplicationState,
    null,
    IDeleteProductDimensionResponseAction
  >
> = (productDimensionId: number) => {
  return async (dispatch: Dispatch) => {
    const requestAction: IDeleteProductDimensionRequestAction = {
      type: Actions.DeleteProductDimensionRequest,
      payload: productDimensionId,
    }
    dispatch(requestAction)
    try {
      const result = await GlnService.deleteProductDimension(productDimensionId)
      const response: IDeleteProductDimensionResponseAction = {
        success: result.success,
        errorMessage: result.errorMessage,
        type: Actions.DeleteProductDimensionResponse,
      }
      return dispatch(response)
    } catch (e) {
      const errorResponse: IDeleteProductDimensionResponseAction = {
        success: false,
        errorMessage: `Error deleting product dimension: ${e.message}`,
        type: Actions.DeleteProductDimensionResponse,
      }
      return dispatch(errorResponse)
    }
  }
}

export const clearErrorActionCreator = (): IClearErrorAction => ({
  type: Actions.ClearError,
})
